import {AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, inject, OnInit, signal, WritableSignal} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {toObservable} from '@angular/core/rxjs-interop';
import {EventService, Product} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {of, BehaviorSubject, combineLatest} from 'rxjs';

import {catchError, distinctUntilChanged, filter, map, switchMap} from 'rxjs/operators';
import '@luigi-project/container';
import {LuigiContainer} from '@luigi-project/container';

import {ConfigService, DcpGlobalMessageService, TransactionType} from '@dcp/configurator';
import {EddlFormSubmitEvent} from '@app/services/analytics/EDDL/events/eddl-form-submit-event';
import {EddlFormViewEvent} from '@app/services/analytics/EDDL/events/eddl-form-view-event';
import {EddlBeaconKey} from '@app/services/analytics/EDDL/models/eddl-adobe-tracking-data';
import {FormDataTrackingService} from '@app/services/analytics/form-data-tracking.service';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {CrmAccountTrackingService} from '@app/services/crm-account-tracking.service';
import {GoogleAutocompleteService} from '@app/services/google-autocomplete.service';
import {TransactionTypeService} from '@app/services/transactionTypes/transaction-type.service';
import {LeadFormLuigiContext} from '@app/product/lead/lead.model';
import {LuigiLeadService} from '@app/product/lead/luigi-lead.service';
import {DcpProductService} from '@app/product/services/dcp-product.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {EddlFormErrorEvent} from '@app/services/analytics/EDDL/events/eddl-form-error-event';

@UntilDestroy()
@Component({
  selector: 'dcp-lead',
  standalone: true,
  templateUrl: './lead.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class LeadComponent implements OnInit, AfterViewInit {

  public siteKey: string;
  public url: string;

  readonly gRecaptchaId: string = 'g-recaptcha-lead-form';

  public container: LuigiContainer;
  public context: WritableSignal<LeadFormLuigiContext> = signal<LeadFormLuigiContext>(undefined);
  public product: WritableSignal<Product> = signal<Product>(undefined);
  public transactionType: WritableSignal<TransactionType> = signal<TransactionType>(undefined);
  public eddlEventCommonContent: WritableSignal<any> = signal<any>(undefined);

  public product$ = toObservable(this.product);
  public transactionType$ = toObservable(this.transactionType);
  public viewInitialized$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private autocompleteService: GoogleAutocompleteService = inject(GoogleAutocompleteService);
  private campaignTrackingService: CampaignTrackingService = inject(CampaignTrackingService);
  private configService: ConfigService = inject(ConfigService);
  private crmAccountTrackingService: CrmAccountTrackingService = inject(CrmAccountTrackingService);
  private dcpGlobalMessageService: DcpGlobalMessageService = inject(DcpGlobalMessageService)
  private dcpProductService = inject(DcpProductService);
  private dcpProductUtilitiesService = inject(DcpProductUtilitiesService);
  private eventService: EventService = inject(EventService);
  private formDataTrackingService: FormDataTrackingService = inject(FormDataTrackingService);
  private leadService: LuigiLeadService = inject(LuigiLeadService);
  private transactionTypeService: TransactionTypeService = inject(TransactionTypeService);

  constructor() {
    this.url = this.configService.getString('webComponents.leadForm');
    this.siteKey = this.configService.getString('RE_CAPTCHA_SITE_KEY');
  }

  ngOnInit() {
    this.container = document.querySelector('#dcp-lead-form');

    this.context.set({
      ...this.leadService.setLuigiContextFromRoute(this.activatedRoute),
      gRecaptchaSiteKey: this.siteKey,
      gRecaptchaId: this.gRecaptchaId,
      campaignData: this.campaignTrackingService.getCampaignData()
    });

    this.activatedRoute.queryParamMap.pipe(
      untilDestroyed(this),
      filter((params) => params.has('entitlementRuleCode')),
      distinctUntilChanged(),
      switchMap((params) =>
        this.dcpProductService.getProductDetailsByCode(params.get('entitlementRuleCode'))
          .pipe(
            untilDestroyed(this),
            map((product) => product.displayProduct),
            catchError((err) => {
              this.dcpGlobalMessageService.addMessage(err, this?.constructor?.name, 'errorLogging');
              return of(undefined);
            })
          )
      ),
    ).subscribe((product) => {
      this.product.set(product)
    });

    this.activatedRoute.queryParamMap.pipe(
      untilDestroyed(this),
      filter((params) => params.has('action')),
      switchMap((params) =>
        this.transactionTypeService.getTransactionTypes$().pipe(
          untilDestroyed(this),
          map((transactions) =>
            transactions.find((transaction) =>
              transaction.code === params.get('action')
            )
          )
        )
      ),
      catchError((err) => {
        this.dcpGlobalMessageService.addMessage(err, this?.constructor?.name, 'errorLogging');
        return of(undefined);
      }),
    ).subscribe((transaction) => {
      this.transactionType.set(transaction)
    })

    combineLatest([
      this.product$,
      this.transactionType$,
      this.viewInitialized$.asObservable()
    ]).pipe(
      untilDestroyed(this),
      distinctUntilChanged(),
      filter(([p, t, v]) => p !== undefined && t !== undefined && v === true),
      map(([product, transactionType, viewInitialized]) =>
        ({
          name: this.formDataTrackingService.generateFormName(transactionType.adobeFormType, product),
          products: [
            {
              ID: this.dcpProductUtilitiesService.getProductId(product),
              name: product.name,
              partnerName: product?.vendor?.name
            }
          ]
        })
      )
    ).subscribe((eventBody) => {
      let event = new EddlFormViewEvent();
      event = ({
        ...event,
        ...eventBody,
        manualBeacon: EddlBeaconKey.ST,
      });
      this.eddlEventCommonContent.set(eventBody);
      setTimeout(() => this.eventService.dispatch(event, EddlFormViewEvent))
    })

    this.crmAccountTrackingService.getAllParametersArray().forEach(param => {
      const paramValue = this.crmAccountTrackingService.getDataFromSessionStorage(param);
      this.context.update((content) => ({...content, [param]: paramValue}))
    })

    this.container?.addEventListener('initialized', () => {
      this.autocompleteService.isLoaded().then(() => {
        this.context.update((content) => ({...content, maps: window.google.maps}))
        this.container.updateContext(this.context());
      });
    });
  }

  ngAfterViewInit() {
    this.viewInitialized$.next(true);

    window.addEventListener('dcp-lead-form:modal:submitSuccess:modal-lead-form', (ev: CustomEvent) => {
      this.triggerEddlFormSubmit(ev?.detail?.leadId!)
    })

    window.addEventListener('dcp-lead-form:modal:submitError:modal-lead-form', (ev: CustomEvent) => {
      this.triggerEddlFormError(ev?.detail?.errorMessage?.error ?? ev?.detail?.errorMessage?.message ?? '')
    })
  }

  triggerEddlFormSubmit(leadId: string) {
    let event: EddlFormSubmitEvent = new EddlFormSubmitEvent();
    event = {
      ...event,
      ...this.eddlEventCommonContent(),
      transactionId: leadId,
    }

    this.eventService.dispatch(event, EddlFormSubmitEvent);
  }

  triggerEddlFormError(errorMessage: string) {
    let event: EddlFormErrorEvent = new EddlFormErrorEvent();
    event = {
      ...event,
      ...this.eddlEventCommonContent(),
    }

    this.eventService.dispatch(event, EddlFormErrorEvent);
  }
}
